import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Globe,
  Activity,
  Shield,
  Key,
  FileText,
  Bell,
  Settings,
  ChevronLeft,
  ChevronRight,
  Server,
  Lock,
  X,
  Mail,
  ClipboardList,
  Network,
  Building2,
  Award,
  Star,
  FileWarning,
  BarChart3,
  Bug,
  ShieldAlert,
  HeartPulse,
  Wrench,
  Send,
  FileSignature,
  ShieldCheck,
  UserPlus,
  BookOpen,
  UserMinus,
  Globe2,
  CalendarDays,
  Ticket,
  HardDrive,
  GitBranch,
  Radar,
  Clock,
  BookMarked,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: Globe, label: 'Domains', href: '/domains' },
  { icon: Activity, label: 'Monitors', href: '/monitors' },
  { icon: Mail, label: 'Email Security', href: '/email-security' },
  { icon: Server, label: 'DNS', href: '/dns' },
  { icon: Shield, label: 'SSL Certificates', href: '/ssl' },
  { icon: Lock, label: 'Secret Vault', href: '/secrets' },
  { icon: FileText, label: 'Status Pages', href: '/status-pages' },
  { icon: Bell, label: 'Alerting', href: '/alerting' },
  { icon: Network, label: 'IPAM', href: '/ipam' },
  { icon: ClipboardList, label: 'Changes', href: '/changes' },
  { icon: FileWarning, label: 'Post-Mortems', href: '/post-mortems' },
  { icon: Star, label: 'CSAT / NPS', href: '/csat' },
  { icon: Building2, label: 'Vendors', href: '/vendors' },
  { icon: Award, label: 'Training', href: '/training' },
  { icon: BarChart3, label: 'Reports', href: '/reports' },
  { icon: Bug, label: 'CVE Feed', href: '/cve-feed' },
  { icon: ShieldAlert, label: 'Vendor Risk', href: '/vendor-risk' },
  { icon: HeartPulse, label: 'Platform Status', href: '/platform-status' },
  { icon: FileSignature, label: 'Contracts & SLA', href: '/contracts' },
  { icon: ShieldCheck, label: 'Cyber Insurance', href: '/cyber-insurance' },
  { icon: Wrench, label: 'IT Tools', href: '/it-tools' },
  { icon: Send, label: 'API Hub', href: '/api-hub' },
  { icon: UserPlus, label: 'Onboarding', href: '/onboarding' },
  { icon: BookOpen, label: 'Playbooks', href: '/playbooks' },
  { icon: UserMinus, label: 'Offboarding', href: '/offboarding' },
  { icon: Globe2, label: 'WordPress', href: '/wordpress' },
  { icon: CalendarDays, label: 'Space Booking', href: '/space-booking' },
  { icon: Ticket, label: 'Tickets', href: '/tickets' },
  { icon: HardDrive, label: 'Infrastructure', href: '/infrastructure' },
  { icon: GitBranch, label: 'DevOps', href: '/devops' },
  { icon: Radar, label: 'Attack Surface', href: '/attack-surface' },
  { icon: Clock, label: 'Timesheets', href: '/timesheets' },
  { icon: BookMarked, label: 'Runbooks', href: '/runbooks' },
  { icon: Users, label: 'Client Portal', href: '/client-portal' },
  { icon: FileText, label: 'Audit Log', href: '/audit-log' },
  { icon: Key, label: 'API Keys', href: '/api-keys' },
  { icon: Settings, label: 'Settings', href: '/settings' },
]

export function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const location = useLocation()

  const handleNavClick = () => {
    // Close mobile menu on navigation
    if (onMobileClose && window.innerWidth < 768) {
      onMobileClose()
    }
  }

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen border-r bg-sidebar transition-all duration-300 ease-in-out',
          // Desktop behavior
          'hidden md:block',
          collapsed ? 'md:w-16' : 'md:w-64',
          // Mobile behavior: slide in/out
          mobileOpen && 'block w-64 animate-in slide-in-from-left duration-300'
        )}
        aria-label="Main navigation"
      >
        <div className="flex h-16 items-center justify-between border-b px-4">
          {/* Logo with smooth transition */}
          <Link 
            to="/dashboard" 
            className={cn(
              "flex items-center gap-2 transition-all duration-300 overflow-hidden",
              collapsed && !mobileOpen ? 'w-0 opacity-0' : 'w-auto opacity-100'
            )}
            onClick={handleNavClick}
          >
            <div className="h-8 w-8 shrink-0 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold">A</span>
            </div>
            <span className="font-semibold text-lg whitespace-nowrap">Arete</span>
          </Link>
          
          {/* Mobile close button */}
          {mobileOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMobileClose}
              className="md:hidden"
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          
          {/* Desktop collapse button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className={cn(
              'hidden md:flex',
              collapsed && 'mx-auto'
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-4rem)]">
          <nav className="flex flex-col gap-1 p-2" role="navigation">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
              const Icon = item.icon
              const showLabels = !collapsed || mobileOpen

              const linkContent = (
                <Link
                  to={item.href}
                  onClick={handleNavClick}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sidebar-foreground transition-all duration-200',
                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:translate-x-0.5',
                    isActive && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium shadow-sm',
                    !showLabels && 'justify-center px-2'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {showLabels && (
                    <span className="transition-opacity duration-200">{item.label}</span>
                  )}
                </Link>
              )

              if (!showLabels) {
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                )
              }

              return <div key={item.href}>{linkContent}</div>
            })}
          </nav>
        </ScrollArea>
      </aside>
    </TooltipProvider>
  )
}
